import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Scroll, PenTool, BookOpenCheck, Landmark, ArrowRight, ArrowUpRight, 
  Menu, X, Zap, Heart, Users, Trophy, School, Clock, 
  MapPin, Phone, Mail, Globe, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, Star, Camera, Shield
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const AcademicClassic: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Landmark;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Landmark;
  };

  const schoolName = previewData?.name || "Oxford Heritage College";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=2070&auto=format&fit=crop"],
      tagline: "The Pursuit of Human Excellence",
      titlePrefix: "Tradition Meets",
      subtitle: "For over a century, we have championed the rigorous study of classical arts and modern sciences to forge leaders of profound character."
    },
    features: [
      { icon: "BookOpenCheck", title: "Intellect", desc: "Rigorous academic inquiry guided by master-scholars." },
      { icon: "Scroll", title: "Tradition", desc: "Respect for historical wisdom and institutional heritage." },
      { icon: "PenTool", title: "Rhetoric", desc: "Mastering the art of effective and moral communication." }
    ],
    programs: [
      { title: "Classical Lyceum", age: "Years 1-8", desc: "Foundational mastery of trivium: grammar, logic, and rhetoric.", img: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?q=80&w=2073&auto=format&fit=crop" },
      { title: "Honours Academy", age: "Years 9-13", desc: "Intensive pre-collegiate training in dual-enrollment pathways.", img: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop" }
    ],
    stats: [
        { value: "EST. 1898", label: "INSTITUTIONAL AGE" },
        { value: "24k+", label: "ALUMNI NETWORK" },
        { value: "100%", label: "IVY PLACEMENT" }
    ],
    news: [
        { title: "The Heritage Medal", date: "JAN 12", desc: "Recognizing outstanding contribution to community service.", img: "https://images.unsplash.com/photo-1525921429624-479b6a29d84c?q=80&w=2070&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'PHILOSOPHY', href: '#features' },
        { label: 'CURRICULUM', href: '#programs' },
        { label: 'ARCHIVES', href: '#gallery' },
        { label: 'REGISTRY', href: '#contact' }
    ],
    team: [
        { name: "Dr. Alistair Sterling", role: "Headmaster", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop" },
        { name: "Julianna Thorne", role: "Dean of Studies", img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1976&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Library", img: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?q=80&w=2070&auto=format&fit=crop", title: "The North Wing" },
        { category: "Campus", img: "https://images.unsplash.com/photo-1498243639359-2cee50821686?q=80&w=2070&auto=format&fit=crop", title: "Assembly Quad" }
    ],
    facilities: [
        { title: "Great Hall", desc: "Formal dining and ceremonial space for students and faculty.", img: "https://images.unsplash.com/photo-1519074063912-ad25b57b9d17?q=80&w=2070&auto=format&fit=crop", icon: "Landmark" },
        { title: "Scriptorium", desc: "Silent study environment dedicated to deep research.", img: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?q=80&w=1973&auto=format&fit=crop", icon: "PenTool" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "CHARACTER BEFORE CAREER.",
          description: previewData?.about?.description || schoolName + " stands as a bulwark of classical thought in a rapidly changing world. We focus on the enduring values that create not just successful professionals, but whole persons.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2071&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "Strategic_Compact.pdf", size: "1.2 MB", type: "PDF" },
          { title: "Statutes_&_Handbooks.pdf", size: "4.5 MB", type: "PDF" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "One Heritage Square, Academic Hill, Nairobi",
        phone: previewData?.contact?.phone || "+123 456 789 000",
        email: previewData?.contact?.email || "registrar@heritage.edu",
        officeHours: previewData?.contact?.officeHours || "MON-FRI: 0800 - 1630",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "Academic Archives",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Institutional Documentation",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Contact Form",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Fill out the form below."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#660000');
        root.style.setProperty('--color-secondary', colors.secondary || '#1c1c1c');
        root.style.setProperty('--color-accent', colors.accent || '#e5e0d5');
        root.style.setProperty('--color-background', colors.background || '#fdfbf7');
        root.style.setProperty('--color-text', colors.text || '#2d2d2d');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Playfair Display'}', serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Inter'}', sans-serif`);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=Inter:wght@400;500;700&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Excise this entry?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="academic-classic bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-primary)] selection:text-white overflow-x-hidden">
      
      {/* Formal Header Section */}
      <header className="py-20 bg-white border-b-2 border-[var(--color-accent)] text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-[8px] bg-[var(--color-primary)]" />
          <div className="container mx-auto px-6 relative z-10">
              <div className="w-20 h-20 mx-auto bg-[var(--color-primary)] rounded-full flex items-center justify-center text-white mb-10 shadow-xl border-4 border-white">
                   <Landmark size={36} />
              </div>
              <h1 className="text-4xl md:text-6xl font-black font-[var(--font-heading)] uppercase tracking-tighter text-[var(--color-secondary)] mb-2">
                  <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
              </h1>
              <div className="flex items-center justify-center gap-6 mb-12">
                  <div className="h-px w-12 bg-[var(--color-primary)]" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-400">Founded MDCCCXCVIII</span>
                  <div className="h-px w-12 bg-[var(--color-primary)]" />
              </div>
          </div>
      </header>

      {/* Nav Ridge */}
      <nav className="sticky top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-[var(--color-accent)] shadow-sm">
          <div className="container mx-auto px-6 py-5 flex justify-center items-center gap-12 font-bold text-[10px] uppercase tracking-[0.3em] text-gray-500">
              {mockData.nav.filter(n => n.visible).map((n, i) => (
                  <a key={i} href={n.href} className="hover:text-[var(--color-primary)] transition-all relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-[var(--color-primary)] hover:after:w-full after:transition-all">{n.label}</a>
              ))}
              <Button variant="outline" className="h-10 px-6 border-[var(--color-primary)] text-[var(--color-primary)] rounded-none font-bold text-[9px] uppercase tracking-widest hover:bg-[var(--color-primary)] hover:text-white transition-all">Enrolment</Button>
          </div>
      </nav>

      {/* Hero Section - The Academy */}
      {mockData.sectionVisibility.hero && (
      <section className="relative py-40 overflow-hidden bg-[var(--color-accent)]/20">
          <div className="container mx-auto px-6">
              <div className="flex flex-col lg:flex-row gap-24 items-center">
                  <div className="lg:w-1/2 order-2 lg:order-1">
                      <div className="relative p-2 border border-[var(--color-accent)] bg-white shadow-2xl skew-y-1 hover:skew-y-0 transition-transform duration-700">
                           <VisualImageEditable 
                              src={mockData.hero.backgroundImage} 
                              className="w-full h-auto grayscale brightness-90 hover:grayscale-0 transition-all duration-1000" 
                              alt="Heritage"
                              onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                              isEditMode={!!isEditMode}
                          />
                          <div className="p-4 text-center text-[10px] font-bold uppercase tracking-widest text-gray-400 italic">Plate No. 001 // The Main Conservatory</div>
                      </div>
                  </div>
                  <div className="lg:w-1/2 order-1 lg:order-2">
                       <span className="text-[var(--color-primary)] font-bold tracking-[0.3em] uppercase text-[10px] mb-8 block">Office of the Headmaster</span>
                       <h2 className="text-6xl md:text-8xl font-black font-[var(--font-heading)] text-[var(--color-secondary)] leading-[0.85] mb-12 tracking-tighter">
                          <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} /><br/>
                          <span className="italic font-normal lowercase tracking-normal text-[var(--color-primary)]">Human Excellence.</span>
                       </h2>
                       <p className="text-2xl font-serif text-gray-600 leading-relaxed mb-16 italic border-l-8 border-[var(--color-primary)] pl-12 py-4">
                          <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
                       </p>
                       <div className="flex gap-4">
                           <Button className="h-16 px-12 bg-[var(--color-primary)] text-white rounded-none font-bold uppercase tracking-widest text-xs hover:brightness-110 transition-all shadow-[8px_8px_0px_var(--color-secondary)]">
                               Admissions
                           </Button>
                           <Button variant="ghost" className="h-16 px-12 border border-[var(--color-primary)]/20 text-[var(--color-primary)] rounded-none font-bold uppercase tracking-widest text-xs hover:bg-[var(--color-primary)] hover:text-white">
                               The Manifesto
                           </Button>
                       </div>
                  </div>
              </div>
          </div>
      </section>
      )}

      {/* Core Pillars - Icons */}
      {mockData.sectionVisibility.features && (
      <section id="features" className="py-40 bg-[var(--color-secondary)] text-[var(--color-accent)]">
          <div className="container mx-auto px-6 text-center">
              <h3 className="text-2xl font-black font-[var(--font-heading)] uppercase mb-20 tracking-widest border-b-2 border-white/10 inline-block pb-8">Principal Pillars</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-24">
                  {mockData.features.map((item, i) => (
                      <div key={i} className="group relative">
                          <div className="mx-auto w-24 h-24 rounded-full border border-white/20 flex items-center justify-center mb-10 group-hover:border-[var(--color-primary)] group-hover:bg-[var(--color-primary)] transition-all duration-500">
                              {React.createElement(item.icon, { size: 40, className: "group-hover:scale-110 transition-transform" })}
                          </div>
                          <h4 className="text-3xl font-black font-[var(--font-heading)] uppercase mb-6 tracking-tight italic">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h4>
                          <p className="text-sm opacity-50 leading-relaxed font-serif max-w-xs mx-auto italic">
                            <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="text-[10px] font-bold mt-8 opacity-20 hover:opacity-100 uppercase tracking-widest">Abolish</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('features', { title: "NEW PILLAR", desc: "Formal description here.", icon: "Scroll" })} className="border-2 border-dashed border-white/10 rounded-full h-24 w-24 mx-auto flex items-center justify-center cursor-pointer hover:border-white transition-all text-white/20">
                          <Plus size={32} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Stats - Historical Metrics */}
      {mockData.sectionVisibility.stats && (
      <section className="py-32 container mx-auto px-6 border-b border-[var(--color-accent)]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-20 text-center">
              {mockData.stats.map((s, i) => (
                  <div key={i} className="group">
                      <div className="text-5xl md:text-6xl font-black text-[var(--color-primary)] mb-4 font-[var(--font-heading)] tracking-tighter">
                        <VisualTextEditable value={s.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-gray-400">
                        <VisualTextEditable value={s.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="mt-8 h-px w-20 bg-gray-100 mx-auto" />
                  </div>
              ))}
          </div>
      </section>
      )}

      {/* Programs - The Curriculum */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="py-40">
          <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-end mb-32 border-b-2 border-gray-100 pb-12">
                   <h2 className="text-6xl font-black font-[var(--font-heading)] text-[var(--color-secondary)] tracking-tighter italic uppercase">Curriculum.</h2>
                   <p className="text-gray-400 font-serif italic max-w-sm text-right leading-relaxed mb-4">"Rigorous training in the classics is the surest path to original thinking in the modern age."</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
                  {mockData.programs.map((p, i) => (
                      <div key={i} className="bg-white p-20 group relative overflow-hidden transition-all duration-700">
                          <div className="absolute top-0 right-0 p-12 text-9xl font-black text-gray-50 uppercase pointer-events-none group-hover:text-[var(--color-primary)]/5 transition-colors">0{i+1}</div>
                          <div className="relative z-10">
                              <div className="text-[10px] font-bold text-[var(--color-primary)] uppercase tracking-widest mb-6 border-l-2 border-[var(--color-primary)] pl-6">{p.age}</div>
                              <h3 className="text-4xl font-black font-[var(--font-heading)] uppercase mb-8 italic text-[var(--color-secondary)]">
                                <VisualTextEditable value={p.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                              </h3>
                              <p className="text-xl font-serif text-gray-500 leading-relaxed mb-12 italic max-w-lg">
                                <VisualTextEditable value={p.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                              </p>
                              <div className="aspect-[21/9] overflow-hidden mb-12 border border-gray-100">
                                   <VisualImageEditable 
                                      src={p.img} alt={p.title}
                                      className="w-full h-full object-cover grayscale brightness-110 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-1000"
                                      onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                                      isEditMode={!!isEditMode}
                                  />
                              </div>
                              <button className="text-[10px] font-black uppercase tracking-[0.4em] flex items-center gap-6 group-hover:gap-12 transition-all">Syllabus Registry <ArrowRight size={20} className="text-[var(--color-primary)]"/></button>
                          </div>
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('programs', { title: "NEW DEPARTMENT", age: "YEARS X-Y", desc: "Scholarly description." })} className="p-20 border-4 border-dashed border-gray-100 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-all text-gray-200">
                          <Plus size={64} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Facilities - The Grounds */}
      {mockData.sectionVisibility.facilities && (
      <section className="py-40 bg-[var(--color-secondary)]">
           <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 items-center">
                    <div className="lg:col-span-4 text-[var(--color-accent)]">
                         <h2 className="text-5xl font-black font-[var(--font-heading)] uppercase mb-12 italic text-white tracking-tighter leading-none">The Academy Grounds.</h2>
                         <p className="text-lg font-serif italic text-white/40 mb-12 leading-loose">Our facilities are designed to mirror the dignity of the pursuits that take place within them.</p>
                         <div className="space-y-12">
                             {mockData.facilities.map((fac, i) => (
                                 <div key={i} className="flex gap-10 group cursor-pointer">
                                     <div className="w-16 h-16 border border-white/10 flex items-center justify-center group-hover:border-[var(--color-primary)] group-hover:bg-[var(--color-primary)] transition-all">
                                         {React.createElement(fac.icon, { size: 28, className: "text-white" })}
                                     </div>
                                     <div>
                                         <h4 className="text-xl font-black font-[var(--font-heading)] uppercase mb-2 group-hover:text-[var(--color-primary)] transition-colors">
                                            <VisualTextEditable value={fac.title} onSave={(v) => handleUpdate(`facilities[${i}].title`, v)} isEditMode={!!isEditMode} />
                                         </h4>
                                         <p className="text-xs font-serif text-white/20 italic line-clamp-2">
                                            <VisualTextEditable value={fac.desc} onSave={(v) => handleUpdate(`facilities[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                                         </p>
                                     </div>
                                 </div>
                             ))}
                         </div>
                    </div>
                    <div className="lg:col-span-8">
                         <div className="grid grid-cols-2 gap-8">
                             {mockData.facilities.map((fac, i) => (
                                 <div key={i} className="aspect-[4/5] overflow-hidden border-8 border-white/5 relative group">
                                      <VisualImageEditable 
                                        src={fac.img} alt={fac.title} 
                                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-[3s]" 
                                        onImageChange={(u) => handleUpdate(`facilities[${i}].img`, u)} 
                                        isEditMode={!!isEditMode}
                                    />
                                      <div className="absolute inset-x-8 bottom-8 p-6 bg-white shadow-2xl opacity-0 group-hover:opacity-100 transition-all translate-y-8 group-hover:translate-y-0">
                                          <div className="text-[9px] font-black uppercase text-gray-400">Section_FAC-00{i+1}</div>
                                      </div>
                                 </div>
                             ))}
                         </div>
                    </div>
                </div>
           </div>
      </section>
      )}

      {/* Gallery - The Archives */}
      {mockData.sectionVisibility.gallery && (
      <section id="gallery" className="py-40 bg-white">
           <div className="container mx-auto px-6">
                <div className="flex justify-center mb-32">
                     <h2 className="text-4xl font-black font-[var(--font-heading)] uppercase tracking-[0.5em] text-gray-200 border-b-4 border-gray-100 pb-8">Archive Prints</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1px bg-gray-100 border border-gray-100 shadow-xl">
                     {mockData.gallery.map((item, i) => (
                         <div key={i} className="aspect-square relative group bg-white overflow-hidden p-4">
                              <VisualImageEditable 
                                src={item.img} alt={item.title}
                                className="w-full h-full object-cover grayscale brightness-90 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-1000"
                                onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                                isEditMode={!!isEditMode}
                            />
                              <div className="absolute inset-0 bg-[var(--color-primary)]/90 opacity-0 group-hover:opacity-100 transition-all p-10 flex flex-col justify-end text-white">
                                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-4 text-white/60">{item.category}</div>
                                  <h4 className="text-2xl font-black italic font-[var(--font-heading)] leading-none">{item.title}</h4>
                              </div>
                         </div>
                     ))}
                </div>
           </div>
      </section>
      )}

      {/* News - The Registry Gazette */}
      {mockData.sectionVisibility.news && (
      <section className="py-40 bg-[var(--color-accent)]/10 px-6">
          <div className="container mx-auto">
              <div className="text-center mb-32">
                   <h2 className="text-7xl md:text-[10rem] font-black font-[var(--font-heading)] text-[var(--color-secondary)] leading-[0.75] tracking-tighter italic uppercase opacity-5">The Gazette.</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-24 items-start">
                  {mockData.news.map((item, i) => (
                      <div key={i} className="group border-t-2 border-[var(--color-primary)] pt-12">
                          <div className="text-[11px] font-black text-[var(--color-primary)] uppercase tracking-widest mb-10 italic">VOL_XXIV // ISSUE_{item.date}</div>
                          <h3 className="text-4xl font-black font-[var(--font-heading)] text-[var(--color-secondary)] mb-12 leading-none italic uppercase tracking-tighter">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`news[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <div className="aspect-[3/2] overflow-hidden mb-12 border border-black/5 relative">
                               <VisualImageEditable 
                                  src={item.img} alt={item.title}
                                  className="w-full h-full object-cover grayscale brightness-110 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000"
                                  onImageChange={(u) => handleUpdate(`news[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute inset-0 border-[15px] border-white/0 group-hover:border-white/20 transition-all pointer-events-none" />
                          </div>
                          <p className="text-lg font-serif text-gray-500 leading-relaxed mb-12 line-clamp-4 italic group-hover:text-black transition-colors">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`news[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <Button variant="ghost" className="p-0 text-[10px] font-black uppercase tracking-widest hover:text-[var(--color-primary)] hover:bg-transparent flex items-center gap-6 group">Read Circular <ArrowRight size={18} className="group-hover:translate-x-4 transition-transform"/></Button>
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Downloads - The Registry Files */}
      {mockData.sectionVisibility.downloads && (
      <section className="py-40 bg-white">
          <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b-2 border-gray-100 pb-12">
                   <h2 className="text-6xl font-black font-[var(--font-heading)] text-[var(--color-secondary)] tracking-tighter italic uppercase">
                        <VisualTextEditable 
                            value={mockData.labels.downloadsTitle} 
                            onSave={(v) => handleUpdate('labels.downloadsTitle', v)} 
                            isEditMode={!!isEditMode} 
                            as="span" 
                        />
                    </h2>
                   {isEditMode && (
                        <span className="text-[10px] font-black uppercase text-gray-400 tracking-[0.3em] mb-4 block">
                            <VisualTextEditable 
                                value={mockData.labels.downloadsSection} 
                                onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                                isEditMode={!!isEditMode} 
                                as="span" 
                            />
                        </span>
                    )}
                    {isEditMode && (
                       <Button onClick={() => addArrayItem('downloads', { title: "New_Registry_Entry.pdf", size: "1.0 MB", type: "PDF" })} className="bg-[var(--color-primary)] text-white rounded-none h-12 px-8 font-bold uppercase tracking-widest text-[9px] hover:brightness-110 shadow-xl">
                           APPEND_ENTRY +
                       </Button>
                   )}
              </div>

              <div className="space-y-1">
                  {mockData.downloads.map((doc, i) => (
                      <div key={i} className="group flex flex-col md:flex-row items-start md:items-center justify-between p-10 border border-gray-100 hover:border-[var(--color-primary)] hover:bg-gray-50 transition-all relative">
                          <div className="flex items-center gap-8 mb-6 md:mb-0">
                              <div className="w-16 h-20 bg-[var(--color-accent)]/30 flex items-center justify-center border-t-4 border-[var(--color-primary)] group-hover:scale-110 transition-transform">
                                  <FileText size={32} className="text-[var(--color-primary)] opacity-40" />
                              </div>
                              <div>
                                  <h4 className="text-2xl font-black font-[var(--font-heading)] text-[var(--color-secondary)] mb-2 uppercase italic tracking-tight">
                                      <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                                  </h4>
                                  <div className="flex gap-4 text-[9px] font-bold uppercase tracking-[0.3em] text-gray-400">
                                      <span>TYPE: <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} /></span>
                                      <div className="w-1 h-1 bg-gray-200 rounded-full my-auto" />
                                      <span>SIZE: <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} /></span>
                                  </div>
                              </div>
                          </div>
                          <button className="flex items-center gap-6 text-[10px] font-black uppercase tracking-[0.4em] text-[var(--color-primary)] border-b border-[var(--color-primary)]/20 pb-2 hover:border-[var(--color-primary)] transition-all">
                              ACCESS_DOCUMENT <Download size={18} />
                          </button>
                          {isEditMode && (
                              <button onClick={() => removeArrayItem('downloads', i)} className="absolute top-4 right-4 text-red-700/20 hover:text-red-700 transition-colors uppercase text-[10px] font-black">Excise</button>
                          )}
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* CTA - Formal Injunction */}
      {mockData.sectionVisibility.cta && (
      <section className="py-64 relative bg-[var(--color-primary)] text-white text-center px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-5 mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
          <div className="container mx-auto relative z-10 max-w-5xl">
              <span className="text-[var(--color-accent)] font-bold tracking-[0.6em] uppercase text-xs mb-10 block">FORGE YOUR HERITAGE</span>
              <h2 className="text-7xl md:text-[12rem] font-black font-[var(--font-heading)] leading-none italic mb-16 uppercase tracking-tighter drop-shadow-2xl">
                 Knowledge. <br/> <span className="underline underline-offset-[20px] decoration-white/20">Virtue.</span>
              </h2>
              <div className="flex flex-col sm:flex-row justify-center gap-10">
                   <Button className="bg-[var(--color-secondary)] text-white rounded-none h-24 px-16 font-black uppercase tracking-widest text-xs hover:scale-105 transition-all shadow-2xl border-4 border-white/10">
                       Request Enrolment
                   </Button>
                   <Button variant="outline" className="border-white/20 text-white rounded-none h-24 px-16 font-black uppercase tracking-widest text-xs hover:bg-white hover:text-[var(--color-primary)] transition-all">
                       Examine Prospectus
                   </Button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - The Archive Office */}
      <footer id="contact" className="bg-white text-[var(--color-secondary)] pt-40 pb-20 px-6 border-t-8 border-[var(--color-primary)]">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-40 items-start">
                  <div className="lg:col-span-5">
                      <div className="flex items-center gap-6 mb-12">
                           <div className="w-16 h-16 bg-[var(--color-primary)] rounded-full flex items-center justify-center text-white border-4 border-white shadow-xl">
                               <Landmark size={32} />
                           </div>
                           <h2 className="text-5xl font-black italic uppercase tracking-tighter font-[var(--font-heading)]">{schoolName}</h2>
                      </div>
                      <p className="text-3xl font-serif font-black leading-tight mb-20 italic text-gray-200">"The foundation of the state is the education of its youth."</p>
                      <div className="flex gap-6">
                          {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                              <a key={i} href="#" className="w-16 h-16 bg-white border-2 border-[var(--color-accent)] rounded-full flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-white transition-all shadow-sm"><Icon size={24} /></a>
                          ))}
                      </div>
                  </div>
                  
                  <div className="lg:col-span-3">
                      <h4 className="text-[11px] font-black tracking-[0.5em] uppercase text-[var(--color-primary)] mb-16 underline underline-offset-8">THE REGISTRY</h4>
                      <ul className="space-y-8 text-sm font-bold uppercase tracking-widest text-gray-400">
                          {mockData.nav.map((item, i) => (
                              <li key={i}><a href={item.href} className="hover:text-[var(--color-primary)] transition-all flex items-center gap-8 group">
                                <div className="w-2 h-2 rounded-full bg-[var(--color-primary)] opacity-0 group-hover:opacity-100" />
                                {item.label}
                                </a></li>
                          ))}
                      </ul>
                  </div>

                  <div className="lg:col-span-4">
                      <h4 className="text-[11px] font-black tracking-[0.5em] uppercase text-[var(--color-primary)] mb-16 underline underline-offset-8">COORDINATES</h4>
                      <div className="space-y-20">
                          <div className="flex gap-12 group">
                              <MapPin size={36} className="text-[var(--color-primary)] opacity-10 shrink-0 group-hover:opacity-100 transition-all" />
                              <p className="text-xl font-black italic tracking-tighter leading-[1.1] uppercase">
                                  <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          <div className="flex gap-12 group">
                              <Phone size={36} className="text-[var(--color-primary)] opacity-10 shrink-0 group-hover:opacity-100 transition-all" />
                              <p className="text-xl font-black italic tracking-tighter leading-none uppercase">
                                  <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                      </div>
                  </div>
              </div>
              
              <div className="pt-20 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-bold uppercase tracking-[0.6em] text-gray-300">
                  <p>&copy; {new Date().getFullYear()} {schoolName} // CLASSICAL_ACCREDITATION // ARCHIVE_MASTER</p>
                  <div className="flex gap-12 italic underline underline-offset-8">
                      <a href="#" className="hover:text-[var(--color-primary)]">Ethics Code</a>
                      <a href="#" className="hover:text-[var(--color-primary)]">Governance</a>
                  </div>
              </div>
          </div>
      </footer>

      {/* Formal Menu UI */}
      {menuOpen && (
          <div className="fixed inset-0 z-[1000] bg-white p-12 flex flex-col animate-in fade-in slide-in-from-top duration-700 overflow-hidden">
               <div className="absolute top-0 right-0 w-full h-[15px] bg-[var(--color-primary)]" />
               <div className="container mx-auto h-full flex flex-col relative z-20">
                   <div className="flex justify-between items-center mb-24 border-b-2 border-gray-100 pb-12">
                       <h2 className="text-5xl font-black italic uppercase tracking-tighter leading-none text-[var(--color-primary)]">{schoolName}</h2>
                       <button onClick={() => setMenuOpen(false)} className="text-gray-900"><X size={64} /></button>
                   </div>
                   <nav className="flex flex-col gap-14">
                       {mockData.nav.filter(n => n.visible).map((n, i) => (
                           <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-6xl md:text-[12rem] font-black uppercase tracking-tighter italic text-gray-100 hover:text-[var(--color-primary)] hover:-translate-y-8 transition-all">
                               {n.label}
                           </a>
                       ))}
                   </nav>
                   <div className="mt-auto flex justify-between items-end border-t-2 border-gray-100 pt-12">
                        <p className="text-[10px] font-black uppercase tracking-[1em] text-gray-200">TRADITION_REBORN</p>
                        <div className="text-[15rem] font-black text-gray-50 leading-none">LEX</div>
                   </div>
               </div>
          </div>
      )}
    </div>
  );
};

export default AcademicClassic;
